import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { conversationAPI, workspaceAPI, presenceAPI, messageAPI, chatFileAPI } from '../lib/api';
import { cn, getInitials } from '../lib/utils';
import { Layout } from '../components/Layout';
import FileAttachment from '../components/discussion/FileAttachment';
import LinkPreview from '../components/discussion/LinkPreview';
import {
    MessageSquare,
    Plus,
    Search,
    Send,
    Smile,
    Paperclip,
    MoreHorizontal,
    Users,
    User,
    X,
    Check,
    Loader2,
    Circle,
    Edit2,
    Trash2,
    Bookmark,
    Image,
    FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
    AlertDialogAction,
} from '../components/ui/alert-dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';

const DirectMessagesPage = () => {
    const { currentWorkspace } = useWorkspace();
    const { user } = useAuth();
    const { markDMRead, refresh: refreshNotifications } = useNotifications();
    const [searchParams, setSearchParams] = useSearchParams();
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [isNewDMOpen, setIsNewDMOpen] = useState(false);
    const [members, setMembers] = useState([]);
    const [onlineUsers, setOnlineUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [editingMessage, setEditingMessage] = useState(null);
    const [editContent, setEditContent] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const messagesEndRef = useRef(null);
    const messageInputRef = useRef(null);
    const wsRef = useRef(null);
    const fileInputRef = useRef(null);

    // Load conversations
    useEffect(() => {
        if (currentWorkspace) {
            loadConversations();
            loadMembers();
            loadPresence();
        } else {
            setLoading(false);
        }
    }, [currentWorkspace]);

    // Load messages and establish WebSocket when conversation changes
    useEffect(() => {
        if (selectedConversation && user) {
            loadMessages();
            connectWebSocket();
        }
        
        // Cleanup WebSocket on conversation change or unmount
        return () => {
            disconnectWebSocket();
        };
    }, [selectedConversation, user]);

    // Scroll to bottom on new messages
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Auto-select conversation from URL param (from notification click)
    useEffect(() => {
        const convId = searchParams.get('conversation');
        if (!convId || conversations.length === 0) return;
        const target = conversations.find(c => c.id === convId);
        if (target && (!selectedConversation || selectedConversation.id !== convId)) {
            setSelectedConversation(target);
            setSearchParams({}, { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversations, searchParams]);

    const loadConversations = async () => {
        try {
            const res = await conversationAPI.getByWorkspace(currentWorkspace.id);
            setConversations(res.data.conversations || []);
        } catch (error) {
            console.error('Failed to load conversations:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadMembers = async () => {
        try {
            const res = await workspaceAPI.getMembers(currentWorkspace.id);
            setMembers(res.data || []);
        } catch (error) {
            console.error('Failed to load members:', error);
        }
    };

    const loadPresence = async () => {
        try {
            const res = await presenceAPI.getOnlineUsers(currentWorkspace.id);
            setOnlineUsers(res.data.map(u => u.user_id) || []);
        } catch (error) {
            console.error('Failed to load presence:', error);
        }
    };

    const loadMessages = async () => {
        if (!selectedConversation) return;
        try {
            const res = await conversationAPI.getMessages(selectedConversation.id, { limit: 50 });
            setMessages(res.data.messages || []);
            // DM messages are returned with read receipt auto-updated by the backend
            // but the conversation list still shows the old unread count until reloaded.
            if (selectedConversation.unread_count > 0) {
                markDMRead(selectedConversation.id);
                setConversations(prev => prev.map(conv =>
                    conv.id === selectedConversation.id
                        ? { ...conv, unread_count: 0 }
                        : conv
                ));
                refreshNotifications();
            }
        } catch (error) {
            console.error('Failed to load messages:', error);
        }
    };

    const connectWebSocket = () => {
        if (!selectedConversation || !user || wsRef.current) return;

        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
        const wsHost = backendUrl.replace(/^https?:\/\//, '');
        const token = localStorage.getItem('token');
        const tokenParam = token ? `?token=${token}` : '';
        const wsUrl = `${wsProtocol}://${wsHost}/ws/dm/${selectedConversation.id}/${user.id}${tokenParam}`;

        try {
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                console.log('DM WebSocket connected:', selectedConversation.id);
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    
                    if (data.type === 'new_message') {
                        setMessages(prev => [...prev, data.message]);
                        // Update conversation list to reflect new message
                        setConversations(prevConvs => 
                            prevConvs.map(conv => 
                                conv.id === selectedConversation.id 
                                    ? { ...conv, last_message_at: new Date().toISOString() }
                                    : conv
                            )
                        );
                    } else if (data.type === 'typing') {
                        // Handle typing indicator (can be implemented later)
                        console.log('User typing:', data.user_name);
                    } else if (data.type === 'read_receipt') {
                        // Handle read receipt (can be implemented later)
                        console.log('Message read by:', data.user_id);
                    }
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error);
                }
            };

            ws.onerror = (error) => {
                console.error('DM WebSocket error:', error);
            };

            ws.onclose = () => {
                console.log('DM WebSocket disconnected');
                wsRef.current = null;
            };
        } catch (error) {
            console.error('Failed to connect WebSocket:', error);
        }
    };

    const disconnectWebSocket = () => {
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const sendMessage = async () => {
        if ((!newMessage.trim() && pendingAttachments.length === 0) || !selectedConversation) return;

        setSendingMessage(true);
        try {
            // Send via WebSocket if connected, otherwise fallback to HTTP
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({
                    type: 'message',
                    content: newMessage,
                    message_type: 'text',
                    mentions: [],
                    attachments: pendingAttachments
                }));
                setNewMessage('');
                setPendingAttachments([]);
            } else {
                // Fallback to HTTP API if WebSocket not connected
                await conversationAPI.sendMessage(selectedConversation.id, {
                    content: newMessage,
                    conversation_id: selectedConversation.id,
                    mentions: [],
                    attachments: pendingAttachments
                });
                setNewMessage('');
                setPendingAttachments([]);
                loadMessages();
            }
        } catch (error) {
            toast.error('Gagal mengirim pesan');
        } finally {
            setSendingMessage(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const addEmoji = (emoji) => {
        setNewMessage(prev => prev + emoji.native);
        setShowEmojiPicker(false);
        messageInputRef.current?.focus();
    };

    const handleFileSelect = async (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;

        setUploadingFile(true);
        const uploaded = [];

        for (const file of files) {
            try {
                const maxSize = 50 * 1024 * 1024;
                if (file.size > maxSize) {
                    toast.error(`${file.name} terlalu besar. Maksimal 50MB`);
                    continue;
                }

                const response = await chatFileAPI.upload(file, null, selectedConversation.id);
                uploaded.push(response.data);
                toast.success(`${file.name} berhasil diupload`);
            } catch (error) {
                console.error('Upload error:', error);
                toast.error(`Gagal upload ${file.name}`);
            }
        }

        setPendingAttachments(prev => [...prev, ...uploaded]);
        setUploadingFile(false);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeAttachment = (attachmentId) => {
        setPendingAttachments(prev => prev.filter(a => a.id !== attachmentId));
    };

    const handleEditMessage = async (messageId) => {
        if (!editContent.trim()) {
            toast.error('Pesan tidak boleh kosong');
            return;
        }

        try {
            const response = await messageAPI.editMessage(messageId, editContent);
            setMessages(prev => prev.map(m => m.id === messageId ? response.data : m));
            setEditingMessage(null);
            setEditContent('');
            toast.success('Pesan berhasil diupdate');
        } catch (error) {
            toast.error('Gagal update pesan');
        }
    };

    const handleDeleteMessage = async (messageId) => {
        if (!window.confirm('Apakah Anda yakin ingin menghapus pesan ini?')) return;

        try {
            await messageAPI.deleteMessage(messageId);
            setMessages(prev => prev.map(m =>
                m.id === messageId
                    ? { ...m, content: '[Pesan dihapus]', is_deleted: true, attachments: [], link_previews: [] }
                    : m
            ));
            toast.success('Pesan berhasil dihapus');
        } catch (error) {
            toast.error('Gagal menghapus pesan');
        }
    };

    const handleBookmarkMessage = async (messageId) => {
        try {
            const response = await messageAPI.toggleBookmark(messageId);
            toast.success(response.data.message);
        } catch (error) {
            toast.error('Gagal bookmark pesan');
        }
    };

    const getOtherParticipant = (conversation) => {
        const otherParticipant = conversation.participants?.find(p => p.id !== user?.id);
        return otherParticipant || conversation.participants?.[0];
    };

    const getConversationName = (conversation) => {
        if (conversation.name) return conversation.name;
        if (conversation.is_group) {
            return conversation.participants?.map(p => p.name).join(', ') || 'Group';
        }
        return getOtherParticipant(conversation)?.name || 'Direct Message';
    };

    const isUserOnline = (userId) => {
        return onlineUsers.includes(userId);
    };

    const handleDeleteConversation = async (conversationId) => {
        const conversation = conversations.find(c => c.id === conversationId);
        setDeleteConfirm({
            type: 'conversation',
            id: conversationId,
            name: getConversationName(conversation)
        });
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        
        try {
            await conversationAPI.deleteConversation(deleteConfirm.id);
            toast.success('Percakapan berhasil dihapus');
            
            if (selectedConversation?.id === deleteConfirm.id) {
                setSelectedConversation(null);
                setMessages([]);
            }
            
            loadConversations();
        } catch (error) {
            console.error('Error deleting conversation:', error);
            toast.error('Gagal menghapus percakapan');
        } finally {
            setDeleteConfirm(null);
        }
    };

    const filteredConversations = conversations.filter(conv => {
        const name = getConversationName(conv).toLowerCase();
        return name.includes(searchQuery.toLowerCase());
    });

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                </div>
            </Layout>
        );
    }

    if (!currentWorkspace) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                        <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>Pilih workspace terlebih dahulu</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="flex h-full bg-white rounded-lg shadow-sm overflow-hidden">
                {/* DM Sidebar */}
                <div className="w-64 border-r border-gray-200 flex flex-col bg-gray-50">
                    {/* Header */}
                    <div className="p-4 border-b border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="font-semibold text-gray-800">Direct Messages</h2>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setIsNewDMOpen(true)}
                            >
                                <Plus className="w-4 h-4" />
                            </Button>
                        </div>
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                placeholder="Cari percakapan..."
                                className="pl-8 h-8 text-sm"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Conversation List */}
                    <div className="flex-1 overflow-y-auto p-2 space-y-1">
                        {filteredConversations.map(conv => {
                            const otherUser = getOtherParticipant(conv);
                            const isOnline = otherUser && isUserOnline(otherUser.id);
                            
                            return (
                                <button
                                    key={conv.id}
                                    onClick={() => setSelectedConversation(conv)}
                                    className={cn(
                                        'w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors',
                                        selectedConversation?.id === conv.id
                                            ? 'bg-brand-primary text-white'
                                            : 'text-gray-700 hover:bg-gray-200'
                                    )}
                                >
                                    <div className="relative flex-shrink-0">
                                        {conv.is_group ? (
                                            <div className="w-9 h-9 rounded-full bg-gray-300 flex items-center justify-center">
                                                <Users className="w-5 h-5 text-gray-600" />
                                            </div>
                                        ) : (
                                            <div className="w-9 h-9 rounded-full bg-brand-primary flex items-center justify-center text-white text-sm font-medium">
                                                {getInitials(otherUser?.name || 'U')}
                                            </div>
                                        )}
                                        {isOnline && (
                                            <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm truncate">
                                            {getConversationName(conv)}
                                        </p>
                                        {conv.last_message_at && (
                                            <p className={cn(
                                                'text-xs truncate',
                                                selectedConversation?.id === conv.id ? 'text-white/70' : 'text-gray-500'
                                            )}>
                                                Pesan terakhir
                                            </p>
                                        )}
                                    </div>
                                    {conv.unread_count > 0 && (
                                        <span className="bg-red-500 text-white text-xs px-1.5 rounded-full">
                                            {conv.unread_count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                        {filteredConversations.length === 0 && (
                            <div className="text-center py-8 text-gray-400">
                                <User className="w-8 h-8 mx-auto mb-2" />
                                <p className="text-sm">Tidak ada percakapan</p>
                                <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => setIsNewDMOpen(true)}
                                >
                                    Mulai percakapan baru
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Chat Area */}
                <div className="flex-1 flex flex-col">
                    {selectedConversation ? (
                        <>
                            {/* Conversation Header */}
                            <div className="h-14 border-b border-gray-200 px-4 flex items-center justify-between bg-white">
                                <div className="flex items-center gap-3">
                                    {selectedConversation.is_group ? (
                                        <div className="w-9 h-9 rounded-full bg-gray-300 flex items-center justify-center">
                                            <Users className="w-5 h-5 text-gray-600" />
                                        </div>
                                    ) : (
                                        <div className="relative">
                                            <div className="w-9 h-9 rounded-full bg-brand-primary flex items-center justify-center text-white text-sm font-medium">
                                                {getInitials(getOtherParticipant(selectedConversation)?.name || 'U')}
                                            </div>
                                            {isUserOnline(getOtherParticipant(selectedConversation)?.id) && (
                                                <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                                            )}
                                        </div>
                                    )}
                                    <div>
                                        <h3 className="font-semibold text-gray-800">
                                            {getConversationName(selectedConversation)}
                                        </h3>
                                        {!selectedConversation.is_group && isUserOnline(getOtherParticipant(selectedConversation)?.id) && (
                                            <p className="text-xs text-green-600">Online</p>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Actions */}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteConversation(selectedConversation.id)}
                                    title="Hapus percakapan"
                                >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                            </div>

                            {/* Messages Area */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                {messages.map((message, index) => (
                                    <DMMessageItem
                                        key={message.id}
                                        message={message}
                                        currentUser={user}
                                        showAvatar={index === 0 || messages[index - 1]?.user_id !== message.user_id}
                                        onEdit={handleEditMessage}
                                        onDelete={handleDeleteMessage}
                                        onBookmark={handleBookmarkMessage}
                                        isEditing={editingMessage?.id === message.id}
                                        editContent={editContent}
                                        setEditContent={setEditContent}
                                        setEditingMessage={setEditingMessage}
                                    />
                                ))}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Pending Attachments Preview */}
                            {pendingAttachments.length > 0 && (
                                <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
                                    <div className="flex flex-wrap gap-2">
                                        {pendingAttachments.map(file => (
                                            <div
                                                key={file.id}
                                                className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-md text-sm"
                                            >
                                                {file.file_type === 'image' ? (
                                                    <Image className="w-4 h-4 text-gray-600" />
                                                ) : (
                                                    <FileText className="w-4 h-4 text-gray-600" />
                                                )}
                                                <span className="text-gray-700 truncate max-w-[150px]">
                                                    {file.filename}
                                                </span>
                                                <button
                                                    onClick={() => removeAttachment(file.id)}
                                                    className="text-gray-400 hover:text-gray-600"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Message Input */}
                            <div className="p-4 border-t border-gray-200 bg-white">
                                <div className="flex items-end gap-2">
                                    <div className="flex-1 relative">
                                        <Textarea
                                            ref={messageInputRef}
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            placeholder={`Kirim pesan ke ${getConversationName(selectedConversation)}...`}
                                            className="min-h-[44px] max-h-32 resize-none pr-20"
                                            rows={1}
                                        />
                                        <div className="absolute right-2 bottom-2 flex items-center gap-1">
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                onChange={handleFileSelect}
                                                className="hidden"
                                                multiple
                                                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
                                            />
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-7 p-0"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={uploadingFile}
                                            >
                                                {uploadingFile ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <Paperclip className="w-4 h-4" />
                                                )}
                                            </Button>
                                            <div className="relative">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-7 p-0"
                                                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                                >
                                                    <Smile className="w-4 h-4" />
                                                </Button>
                                                {showEmojiPicker && (
                                                    <div className="absolute bottom-10 right-0 z-50">
                                                        <Picker
                                                            data={data}
                                                            onEmojiSelect={addEmoji}
                                                            theme="light"
                                                            previewPosition="none"
                                                            skinTonePosition="none"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <Button
                                        onClick={sendMessage}
                                        disabled={(!newMessage.trim() && pendingAttachments.length === 0) || sendingMessage}
                                        className="h-11"
                                    >
                                        {sendingMessage ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Send className="w-4 h-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-gray-500">
                            <div className="text-center">
                                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                <p>Pilih percakapan untuk memulai chat</p>
                                <Button
                                    variant="outline"
                                    className="mt-4"
                                    onClick={() => setIsNewDMOpen(true)}
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Mulai Percakapan Baru
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                {/* New DM Modal */}
                <NewDMModal
                    isOpen={isNewDMOpen}
                    onClose={() => setIsNewDMOpen(false)}
                    workspaceId={currentWorkspace?.id}
                    currentUserId={user?.id}
                    members={members}
                    onCreated={(conv) => {
                        setConversations(prev => [conv, ...prev]);
                        setSelectedConversation(conv);
                        setIsNewDMOpen(false);
                    }}
                />
            </div>

            {/* Delete Confirmation Modal */}
            <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Hapus Percakapan?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Apakah Anda yakin ingin menghapus percakapan dengan <strong>{deleteConfirm?.name}</strong>? 
                            Semua pesan akan terhapus permanen. Tindakan ini tidak dapat dibatalkan.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setDeleteConfirm(null)}>
                            Batal
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDelete}>
                            Hapus
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
};

// DM Message Item Component
const DMMessageItem = ({ message, currentUser, showAvatar, onEdit, onDelete, onBookmark, isEditing, editContent, setEditContent, setEditingMessage }) => {
    const isOwn = message.user_id === currentUser?.id;

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
    };

    if (isEditing) {
        return (
            <div className="flex gap-3 px-4 py-2 bg-yellow-50 rounded-lg">
                <div className="flex-1">
                    <Textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="min-h-[60px]"
                    />
                    <div className="flex gap-2 mt-2">
                        <Button size="sm" onClick={() => onEdit(message.id)}>
                            <Check className="w-4 h-4 mr-1" /> Simpan
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingMessage(null)}>
                            Batal
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={cn(
            'group flex gap-3 px-4 py-1 hover:bg-gray-50',
            isOwn && 'flex-row-reverse'
        )}>
            {/* Avatar */}
            {showAvatar ? (
                <div className={cn(
                    'w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0',
                    isOwn ? 'bg-blue-500' : 'bg-brand-primary'
                )}>
                    {getInitials(message.user?.name || 'U')}
                </div>
            ) : (
                <div className="w-9 flex-shrink-0" />
            )}

            {/* Content */}
            <div className={cn(
                'max-w-[70%]',
                isOwn && 'text-right'
            )}>
                {showAvatar && (
                    <div className={cn(
                        'flex items-baseline gap-2 mb-0.5',
                        isOwn && 'flex-row-reverse'
                    )}>
                        <span className="font-semibold text-gray-900 text-sm">
                            {message.user?.name || 'Unknown'}
                        </span>
                        <span className="text-xs text-gray-400">
                            {formatTime(message.created_at)}
                        </span>
                        {message.is_edited && (
                            <span className="text-xs text-gray-400">(diedit)</span>
                        )}
                    </div>
                )}
                
                <div>
                    {/* Text Content */}
                    <div className={cn(
                        'inline-block px-4 py-2 rounded-2xl',
                        isOwn ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-800'
                    )}>
                        {message.is_deleted ? (
                            <p className="text-xs italic opacity-70">Pesan dihapus</p>
                        ) : (
                            <p className="whitespace-pre-wrap break-words text-sm">{message.content}</p>
                        )}
                    </div>

                    {/* Attachments */}
                    {message.attachments && message.attachments.length > 0 && (
                        <div className="mt-2 space-y-2">
                            {message.attachments.map((attachment, idx) => (
                                <FileAttachment key={idx} attachment={attachment} />
                            ))}
                        </div>
                    )}

                    {/* Link Previews */}
                    {message.link_previews && message.link_previews.length > 0 && (
                        <div className="mt-2 space-y-2">
                            {message.link_previews.map((preview, idx) => (
                                <LinkPreview key={idx} preview={preview} />
                            ))}
                        </div>
                    )}
                </div>

                {/* Message Actions (only show on hover for own messages) */}
                {isOwn && !message.is_deleted && (
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity mt-1">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 px-2">
                                    <MoreHorizontal className="w-3 h-3" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align={isOwn ? "end" : "start"}>
                                <DropdownMenuItem onClick={() => onBookmark(message.id)}>
                                    <Bookmark className="w-4 h-4 mr-2" />
                                    Bookmark
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                                    setEditingMessage(message);
                                    setEditContent(message.content);
                                }}>
                                    <Edit2 className="w-4 h-4 mr-2" />
                                    Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                    onClick={() => onDelete(message.id)}
                                    className="text-red-600"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Hapus
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                )}
            </div>
        </div>
    );
};

// New DM Modal
const NewDMModal = ({ isOpen, onClose, workspaceId, currentUserId, members, onCreated }) => {
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);

    const filteredMembers = members.filter(m => 
        m.user_id !== currentUserId &&
        ((m.user_name || m.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
         (m.user_email || m.email || '').toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const handleSubmit = async () => {
        if (selectedUsers.length === 0) {
            toast.error('Pilih minimal 1 user');
            return;
        }

        setLoading(true);
        try {
            const res = await conversationAPI.create(workspaceId, selectedUsers);
            toast.success('Percakapan dibuat');
            onCreated(res.data);
            setSelectedUsers([]);
            setSearchQuery('');
        } catch (error) {
            toast.error('Gagal membuat percakapan');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Direct Message Baru</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Cari user..."
                            className="pl-9"
                        />
                    </div>

                    {/* Selected users */}
                    {selectedUsers.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {selectedUsers.map(userId => {
                                const member = members.find(m => m.user_id === userId);
                                return (
                                    <span
                                        key={userId}
                                        className="inline-flex items-center gap-1 px-2 py-1 bg-brand-primary/10 text-brand-primary rounded-full text-sm"
                                    >
                                        {member?.user_name || member?.name}
                                        <button
                                            onClick={() => setSelectedUsers(prev => prev.filter(id => id !== userId))}
                                            className="hover:bg-brand-primary/20 rounded-full p-0.5"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                );
                            })}
                        </div>
                    )}

                    {/* User list */}
                    <div className="max-h-60 overflow-y-auto space-y-1 border rounded-md p-2">
                        {filteredMembers.map(member => (
                            <button
                                key={member.user_id}
                                onClick={() => {
                                    if (selectedUsers.includes(member.user_id)) {
                                        setSelectedUsers(prev => prev.filter(id => id !== member.user_id));
                                    } else {
                                        setSelectedUsers(prev => [...prev, member.user_id]);
                                    }
                                }}
                                className={cn(
                                    'w-full flex items-center gap-3 p-2 rounded-md text-left transition-colors',
                                    selectedUsers.includes(member.user_id)
                                        ? 'bg-brand-primary/10'
                                        : 'hover:bg-gray-50'
                                )}
                            >
                                <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center text-white text-sm font-medium">
                                    {getInitials(member.user_name || member.name || 'U')}
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium text-sm">{member.user_name || member.name}</p>
                                    <p className="text-xs text-gray-500">{member.user_email || member.email}</p>
                                </div>
                                {selectedUsers.includes(member.user_id) && (
                                    <Check className="w-5 h-5 text-brand-primary" />
                                )}
                            </button>
                        ))}
                        {filteredMembers.length === 0 && (
                            <p className="text-sm text-gray-400 text-center py-4">Tidak ada user ditemukan</p>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={onClose}>
                            Batal
                        </Button>
                        <Button onClick={handleSubmit} disabled={loading || selectedUsers.length === 0}>
                            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {selectedUsers.length > 1 ? 'Buat Group DM' : 'Mulai Chat'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default DirectMessagesPage;
